import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

import { colors, defaultInputSX, defaultButtonSX } from '@src/styles';
import { CanonicalItemDTO } from '@src/services/automations/modules/v1/canonicals/dto/canonicals.get.response.dto';

export interface CanonicalDialogProps {
  open: boolean;
  isCreating: boolean;
  selectedCanonical: CanonicalItemDTO | null;
  onSave: (canonicalData: Partial<CanonicalItemDTO>) => void;
  onCancel: () => void;
}

export const CanonicalDialog = ({ open, isCreating, selectedCanonical, onSave, onCancel }: CanonicalDialogProps) => {
  const [editingKey, setEditingKey] = useState('');
  const [editingDescription, setEditingDescription] = useState('');
  const [editingResourceId, setEditingResourceId] = useState('');

  useEffect(() => {
    if (selectedCanonical) {
      setEditingKey(selectedCanonical.key || '');
      setEditingDescription(selectedCanonical.description || '');
      setEditingResourceId(selectedCanonical.resourceId ? JSON.stringify(selectedCanonical.resourceId, null, 2) : '');
    } else {
      setEditingKey('');
      setEditingDescription('');
      setEditingResourceId('');
    }
  }, [selectedCanonical]);

  const handleSave = () => {
    try {
      const resourceId = JSON.parse(editingResourceId);
      onSave({
        key: editingKey,
        description: editingDescription,
        resourceId,
      });
    } catch {
      alert('Invalid JSON format for resource ID');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.background.primary,
          border: `1px solid ${colors.background.secondary}`,
        },
      }}
    >
      <DialogTitle sx={{ color: colors.primary.white, fontFamily: 'inter' }}>
        {isCreating ? 'Criar Novo Fluxo Canônico' : 'Editar Fluxo Canônico'}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <TextField
          fullWidth
          label="Key"
          value={editingKey}
          onChange={(e) => setEditingKey(e.target.value)}
          sx={defaultInputSX}
          helperText="Identificador único do fluxo canônico"
        />
        <TextField
          fullWidth
          label="Descrição"
          value={editingDescription}
          onChange={(e) => setEditingDescription(e.target.value)}
          multiline
          rows={3}
          sx={defaultInputSX}
        />
        <TextField
          fullWidth
          label="Resource ID (JSON)"
          value={editingResourceId}
          onChange={(e) => setEditingResourceId(e.target.value)}
          multiline
          rows={8}
          sx={defaultInputSX}
          helperText="JSON object com informações do resource"
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onCancel}
          startIcon={<CancelIcon />}
          sx={{
            ...defaultButtonSX,
            color: colors.primary.white,
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          startIcon={<SaveIcon />}
          variant="contained"
          sx={{
            ...defaultButtonSX,
            backgroundColor: colors.primary.yellow,
            color: colors.background.primary,
            '&:hover': {
              backgroundColor: colors.primary.yellow,
              opacity: 0.8,
            },
          }}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
